import React from "react";
import { Link } from "react-router-dom";
import Nav from "../components/Nav";
import Footer from "../components/Footer";
import { ArrowRight, QrCode, Scan, Users, Lightning, ShieldCheck } from "@phosphor-icons/react";

const steps = [
  { n: "01", title: "Pick up a card", desc: "Buy a Curb Crew card at your local stockist. The QR code is covered by a sticker on the front." },
  { n: "02", title: "Scan + activate", desc: "Peel off the sticker on the front to reveal the QR, scan it with your phone, fill in your name, skills, photo and a private phone. Card goes live in 60 seconds." },
  { n: "03", title: "Wear or share your card", desc: "Anyone with a phone can scan the QR and see your skills, photo and location. Your phone stays private." },
  { n: "04", title: "Get leads, your way", desc: "Visitors leave their name and number. You get notified, you call them — on your time. No subscriptions." },
];

const features = [
  { icon: QrCode, title: "Unique QR per card", desc: "Every code is unique, short, scannable in bright sunlight." },
  { icon: Scan, title: "No app needed", desc: "Works with any native phone camera. Profile opens in the browser." },
  { icon: Lightning, title: "60-second setup", desc: "Scan, fill in your details, photo, password. Live the moment you tap Activate." },
  { icon: ShieldCheck, title: "Phone stays private", desc: "Your phone number is never public. Visitors leave theirs — you choose who to call back." },
];

export default function Home() {
  return (
    <div>
      <Nav />

      {/* Hero */}
      <section className="border-b border-black" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 grid grid-cols-1 md:grid-cols-12 gap-0">
          <div className="md:col-span-7 md:border-r border-black py-14 sm:py-20 md:pr-10">
            <div className="label-tag mb-6 inline-block bg-[color:var(--cc-yellow)] px-2 py-1">
              Made in South Africa · Smart business cards
            </div>
            <h1 className="font-display font-black tracking-tighter text-4xl sm:text-5xl lg:text-7xl leading-[0.95]">
              Smart card. Scan. <span className="bg-[color:var(--cc-yellow)] px-2">Connect.</span>
            </h1>
            <p className="mt-6 text-lg max-w-xl text-neutral-700">
              Pick up a Curb Crew card at your local store. Peel off the sticker on the front to reveal the QR, scan with your phone and fill in your details. Your card is live in 60 seconds. One-off cost. No subscriptions. Ever.
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <Link to="/lookup" className="bg-black text-white label-tag px-6 py-4 brutal-btn flex items-center gap-2" data-testid="hero-lookup-btn">
                Find a Curb Crew <ArrowRight size={16} weight="bold" />
              </Link>
              <Link to="/cardholder/login" className="bg-[color:var(--cc-yellow)] label-tag px-6 py-4 brutal-btn flex items-center gap-2" data-testid="hero-mycard-btn">
                Manage my card <ArrowRight size={16} weight="bold" />
              </Link>
              <Link to="/cards" className="bg-white border border-black label-tag px-6 py-4 brutal-btn flex items-center gap-2" data-testid="hero-cards-btn">
                Compare card types <ArrowRight size={16} weight="bold" />
              </Link>
            </div>

            <div className="mt-12 grid grid-cols-3 gap-6 max-w-lg">
              {[
                { k: "One-off", v: "No subs" },
                { k: "10 sec", v: "Scan to view" },
                { k: "0 apps", v: "to install" },
              ].map((s) => (
                <div key={s.k} className="border-t-2 border-black pt-3">
                  <div className="font-display font-black text-2xl">{s.k}</div>
                  <div className="label-tag text-neutral-600 mt-1">{s.v}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="md:col-span-5 relative overflow-hidden bg-[color:var(--cc-bg-2)] py-14 md:py-0 flex items-center justify-center">
            <div className="absolute inset-0 hatched opacity-[0.04]" />
            <div className="relative z-10 p-8 md:p-12 w-full">
              <div className="bg-white brutal-border brutal-shadow p-6 max-w-sm mx-auto">
                <div className="flex items-center justify-between mb-4">
                  <span className="label-tag">Curb Crew · DEMO</span>
                  <span className="font-mono-cc text-xs">#CC-8KT2</span>
                </div>
                <div className="aspect-square bg-white border border-black grid place-items-center p-4">
                  <svg viewBox="0 0 100 100" className="w-full h-full">
                    {/* Stylized QR */}
                    <rect width="100" height="100" fill="#fff" />
                    {Array.from({ length: 13 }).map((_, i) =>
                      Array.from({ length: 13 }).map((__, j) => {
                        const seed = (i * 7 + j * 13 + i * j) % 5;
                        return seed === 0 ? (
                          <rect key={`${i}-${j}`} x={8 + j * 6.5} y={8 + i * 6.5} width="6" height="6" fill="#0a0a0a" />
                        ) : null;
                      })
                    )}
                    <rect x="8" y="8" width="20" height="20" fill="none" stroke="#0a0a0a" strokeWidth="3" />
                    <rect x="72" y="8" width="20" height="20" fill="none" stroke="#0a0a0a" strokeWidth="3" />
                    <rect x="8" y="72" width="20" height="20" fill="none" stroke="#0a0a0a" strokeWidth="3" />
                  </svg>
                </div>
                <div className="mt-4">
                  <div className="font-display font-black text-xl">Thabo Mokoena</div>
                  <div className="label-tag text-neutral-600 mt-1">Plumber · Electrician</div>
                </div>
                <div className="mt-3 bg-[color:var(--cc-yellow)] label-tag px-3 py-2 border border-black inline-block">
                  Scan to view profile
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Card types teaser — points to /cards for the full comparison.
          Added Feb 2026 to support the multi-card market pivot. */}
      <section className="border-b border-black bg-white" data-testid="card-types-teaser">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-20">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
            <div>
              <div className="label-tag mb-3">Six cards, one platform</div>
              <h2 className="font-display font-black text-3xl sm:text-5xl tracking-tighter max-w-2xl">
                Pick the card that fits your hustle.
              </h2>
            </div>
            <Link to="/cards" className="label-tag px-4 py-3 border border-black brutal-btn flex items-center gap-2" data-testid="teaser-compare-link">
              Compare all six <ArrowRight size={14} weight="bold" />
            </Link>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {[
              { id: "curb_crew",      title: "Curb Crew",      blurb: "Tips for car guards, waiters, vendors.", accent: "bg-[color:var(--cc-yellow)]" },
              { id: "curb_pro",       title: "Curb Pro",       blurb: "Tradies, plumbers, electricians.", accent: "bg-black text-white" },
              { id: "curb_assets",    title: "Curb Retailer",  blurb: "Shopfront, catalogue, stock.", accent: "bg-[#C2410C] text-white" },
              { id: "curb_medic",     title: "Curb Medic",     blurb: "Medical, allergy, pet ID.", accent: "bg-[#DC2626] text-white" },
              { id: "curb_traveller", title: "Curb Traveller", blurb: "Trip ID + driver. Coming soon.", accent: "bg-[#0EA5E9] text-white" },
              { id: "curb_auto",      title: "Curb Auto",      blurb: "Vehicles + logbook. Coming soon.", accent: "bg-[#16A34A] text-white" },
            ].map((t) => (
              <Link
                key={t.id}
                to="/cards"
                className={`border-2 border-black p-3 brutal-shadow brutal-btn ${t.accent}`}
                data-testid={`teaser-card-${t.id}`}
              >
                <div className="font-display font-black text-base leading-tight">{t.title}</div>
                <div className="text-[11px] mt-1 opacity-90 leading-snug">{t.blurb}</div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Twin Matrix teaser — asset-sticker explainer */}
      <section className="border-b border-black bg-[color:var(--cc-bg-2)]" data-testid="twin-matrix-teaser">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-20 grid grid-cols-1 md:grid-cols-12 gap-8 items-end">
          <div className="md:col-span-8">
            <div className="label-tag mb-3">Asset stickers</div>
            <h2 className="font-display font-black text-3xl sm:text-5xl tracking-tighter max-w-2xl">
              Why this sticker can&apos;t be faked.
            </h2>
            <p className="mt-4 text-neutral-700 max-w-xl">
              Curb Pro and Curb Retailer stickers hide a signed QR behind a tiny one your phone actually scans. Watch Twin Matrix to see how.
            </p>
          </div>
          <div className="md:col-span-4 md:flex md:justify-end">
            <Link to="/twin-matrix" className="label-tag px-4 py-3 border border-black brutal-btn inline-flex items-center gap-2 bg-white" data-testid="teaser-twin-matrix-link">
              Watch Twin Matrix <ArrowRight size={14} weight="bold" />
            </Link>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="border-b border-black bg-white" data-testid="how-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-24">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-12">
            <div>
              <div className="label-tag mb-3">How it works</div>
              <h2 className="font-display font-black text-3xl sm:text-5xl tracking-tighter max-w-xl">
                From lanyard to listing in four steps.
              </h2>
            </div>
            <div className="font-mono-cc text-xs text-neutral-500">CURB-CARD / PROCESS_V1</div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 border-l border-t border-black">
            {steps.map((s) => (
              <div key={s.n} className="border-r border-b border-black p-6 md:p-8 bg-white">
                <div className="font-mono-cc text-sm text-neutral-500">{s.n}</div>
                <div className="font-display font-black text-xl mt-2">{s.title}</div>
                <p className="mt-3 text-neutral-700">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="border-b border-black" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-24 grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <div className="label-tag mb-3">Why Curb Crew</div>
            <h2 className="font-display font-black text-3xl sm:text-5xl tracking-tighter">
              Built for the <span className="underline decoration-[color:var(--cc-yellow)] decoration-8 underline-offset-4">curb</span>, not the cloud.
            </h2>
            <p className="mt-6 text-neutral-700 max-w-md">
              We designed it for the pavements, taxi ranks and hardware stores
              where real services get found — fast, offline-friendly, and honest.
            </p>
          </div>
          <div className="md:col-span-8 grid grid-cols-1 sm:grid-cols-2 gap-0 border-l border-t border-black">
            {features.map((f) => {
              const Icon = f.icon;
              return (
                <div key={f.title} className="border-r border-b border-black p-6 md:p-8 bg-white">
                  <Icon size={28} weight="bold" />
                  <div className="font-display font-black text-lg mt-3">{f.title}</div>
                  <p className="text-neutral-700 mt-2">{f.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-b border-black bg-[color:var(--cc-bg-2)]" data-testid="cta-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-24">
          <div className="bg-white border border-black p-8 md:p-12 text-center max-w-2xl mx-auto">
            <div className="label-tag mb-3 flex items-center justify-center gap-2"><Users size={14} weight="bold" /> For service providers</div>
            <h3 className="font-display font-black text-3xl md:text-4xl tracking-tight">Get seen. Get called.</h3>
            <p className="mt-3 text-neutral-700 max-w-md mx-auto">
              Pick up a Curb Crew card from your nearest stockist. Activate in 60 seconds. Anyone with a phone can find you.
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <Link to="/lookup" className="label-tag px-5 py-3 border border-black brutal-btn" data-testid="cta-lookup-btn">
                Find a Curb Crew <ArrowRight size={14} weight="bold" className="ml-2 inline" />
              </Link>
              <Link to="/cardholder/login" className="label-tag px-5 py-3 bg-black text-white brutal-btn" data-testid="cta-mycard-btn">
                Manage my card <ArrowRight size={14} weight="bold" className="ml-2 inline" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
